import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { ModuleValidationSchema } from "../../Utils/Models";
import { FormsIds } from "../../Utils/Constants";
import { Autocomplete, Grid, TextField, Typography } from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { ModuleFormProps } from "../../Utils/Types";
import DropzoneComponent from "../DropzoneComponent";
import {
  getPreviewPaths,
  getValueForAutocomplete,
  isDefined,
  onFormError,
} from "../../Utils/Utils";
import { endpoints } from "../../Utils/UrlEnum";
import { postData } from "../../Services/postData";
import {  useEffect } from "react";
import { updateData } from "../../Services/updateData";
import VideoUploadField from "./VideoUploadField";

export default function ModuleForm(props: ModuleFormProps) {
  const { courses, contextValue } = props;
  const { control, handleSubmit, reset, watch, setValue } = useForm({
    resolver: yupResolver(ModuleValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    context: undefined,
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });

  /**
   * Initialize form with context value
   */
  useEffect(() => {
    if (isDefined(contextValue)) {
      reset(contextValue);
    }
  }, []);

  /**
   * Handle document files change - preserve document objects with IDs
   */
  const handleDocumentIdsChange = (updatedFiles: any[]) => {
    const currentDocuments = watch("documentIds") || [];

    // Map files to preserve original document objects with _id
    const mappedFiles = updatedFiles.map((file) => {
      if (file instanceof File) {
        return file;
      }

      // Find original document object by path
      const originalDoc = currentDocuments.find(
        (doc: any) =>
          (typeof doc === "object" && doc?.document === file) ||
          (typeof doc === "object" && `/${doc?.document}` === file) ||
          (typeof doc === "object" && doc?.document === file.replace(/^\//, ''))
      );

      return originalDoc && typeof originalDoc === "object" && originalDoc?._id
        ? originalDoc
        : file;
    });

    setValue("documentIds", mappedFiles);
  };

  const onSubmit = (model: any) => {
    model._id ? updateElement(model) : createElement(model);
  };

  const updateElement = async (model: any) => {
    const url = `${endpoints.modules}/${model._id}`;
    const formData = formatData(model);

    try {
      const res = (await updateData(url, formData)) as any;
      if (!res || res?.error) {
        console.error("Error response:", res);
        return;
      }
      if (props.onClose) props.onClose();
    } catch (error) {
      console.error("Error creating class:", error);
    }
  };

  const formatData = (model: any) => {
    const formData = new FormData();

    model.mainImageId = model?.mainImageId;
    model.courseId = model?.courseId?._id || model?.courseId;
    model.videos = JSON.stringify(model.videos);

    // Special handling for documentIds
    if (Array.isArray(model.documentIds)) {
      const existingDocIds: string[] = [];
      const newFiles: File[] = [];

      model.documentIds.forEach((item: any) => {
        if (item instanceof File) {
          newFiles.push(item);
        } else if (typeof item === "object" && item?._id) {
          // Document object with _id
          existingDocIds.push(item._id);
        } else if (typeof item === "string" && item.length === 24) {
          // Likely an ObjectId string (24 characters)
          existingDocIds.push(item);
        }
      });

      // Append new files
      newFiles.forEach((file) => {
        formData.append("documentIds", file, file.name);
      });

      // Append existing document IDs as a JSON array
      if (existingDocIds.length > 0) {
        formData.append("existingDocumentIds", JSON.stringify(existingDocIds));
      }

      // Remove documentIds from model to avoid double processing
      delete model.documentIds;
    }

    // Process remaining fields
    Object.keys(model).forEach((key) => {
      const value = model[key];

      if (Array.isArray(value) && value.some((item) => item instanceof File)) {
        // Append each file individually
        value.forEach((file) => {
          if (file instanceof File) {
            formData.append(key, file, file.name);
          }
        });
      } else {
        formData.append(key, value);
      }
    });

    return formData;
  };

  const createElement = async (model: any) => {
    const url = `${endpoints.modules}`;
    const formData = formatData(model);
    try {
      const res = (await postData(url, formData)) as any;
      if (!res || res?.error) {
        console.error("Error response:", res);
        return;
      }
      if (props.onClose) props.onClose();
    } catch (error) {
      console.error("Error creating class:", error);
    }
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "videos",
  });

  return (
    <>
      <form
        id={FormsIds.moduleForm}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit, onFormError)(e);
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name={`name`}
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  {...field}
                  name="name"
                  id="name"
                  label={Vocabulary.name}
                  fullWidth
                  onChange={(event) => field.onChange(event.target.value)}
                  variant="outlined"
                  error={!!error}
                  helperText={error ? error.message : ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name={`sortNumber`}
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  {...field}
                  name="sortNumber"
                  id="sortNumber"
                  label={Vocabulary.sortNumber}
                  type="number"
                  fullWidth
                  onChange={(event) => field.onChange(event.target.value)}
                  variant="outlined"
                  error={!!error}
                  helperText={error ? error.message : ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Controller
              name={`courseId`}
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  size="small"
                  disableClearable
                  id={`courseId`}
                  fullWidth={true}
                  freeSolo={false}
                  disablePortal
                  noOptionsText={Vocabulary.noResultsFound}
                  isOptionEqualToValue={(option: any, value: any) => {
                    return option?._id === value?._id;
                  }}
                  getOptionLabel={(option: any) => option?.name}
                  options={courses || []}
                  value={getValueForAutocomplete(courses, field.value) ?? null}
                  onChange={(_event: any, value: any) => {
                    field.onChange(value?._id);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        rows: 4,
                        multiline: false,
                      }}
                      size="medium"
                      fullWidth
                      name={`courseId`}
                      label={Vocabulary.course}
                      variant="outlined"
                      error={!!error}
                      helperText={error ? error.message : ""}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Typography variant="h5">{Vocabulary.mainImage}</Typography>
            <Controller
              name={`mainImageId`}
              control={control}
              render={({ field: { ref, ...field } }) => (
                <DropzoneComponent
                  onSave={(e) => field.onChange(e)}
                  maxFiles={1}
                  paths={getPreviewPaths(field.value)}
                  key={Array.isArray(field.value) ? field.value.join(",") : ""}
                  altText={Vocabulary.mainImage}
                />
              )}
            />
          </Grid>
          {/* Videos */}
          <Grid item xs={12} sm={12} md={12}>
            <VideoUploadField
              control={control}
              fields={fields}
              append={append}
              remove={remove}
            />
          </Grid>
          {/* PDF Documents */}
          <Grid item xs={12} sm={12} md={12}>
            <Typography variant="h5">{Vocabulary.content}</Typography>
            <Controller
              name="documentIds"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <DropzoneComponent
                  altText={Vocabulary.content}
                  maxFiles={10}
                  multiple={true}
                  acceptedFileTypes={{
                    "application/pdf": [],
                  }}
                  onSave={handleDocumentIdsChange}
                  paths={getPreviewPaths(field.value)}
                  isPreview={true}
                />
              )}
            />
          </Grid>
        </Grid>
      </form>
    </>
  );
}
